import type { VerifyReinitializableResult } from "./ports";
import { hasValidMultipartParts } from "./multipartValidation";

export type CompletionReconcileOutcome = "alreadyComplete" | "lost" | "transient";
type CompletionOutcome = "success" | "defer" | "fail-fast" | "reinitialize" | "bytes-gone";
export type LostRecoveryPolicy = "reinitialize" | "fail-fast";

interface CompletionTransitionInput {
	reconcile: CompletionReconcileOutcome;
	verifyReinitializable: VerifyReinitializableResult;
	lostRecoveryPolicy: LostRecoveryPolicy;
	reinitializationFailure?: "buffers-missing";
}

interface FinalizeMultipartCompletionOptions<Session> {
	session: Session;
	parts: { ETag: string; PartNumber: number }[];
	totalChunks: number;
	ownsLease: (session: Session) => Promise<boolean>;
	completeMultipartUpload: (parts: { ETag: string; PartNumber: number }[]) => Promise<void>;
	markTerminalComplete: (session: Session) => Promise<void>;
	cleanupCompleted: (session: Session) => Promise<void>;
	onCleanupError?: (error: unknown) => void;
	onOwnershipLost?: (phase: "pre-complete" | "post-complete", session: Session) => void;
}

interface MultipartCompletionRequestOptions {
	completeMultipartUpload(): Promise<{ alreadyComplete: boolean }>;
	classifyCompletion(error: unknown): "lost" | "transient" | "fatal";
	verifyReinitializable(): Promise<VerifyReinitializableResult>;
	lostRecoveryPolicy: LostRecoveryPolicy;
	normalize(error: unknown): Error;
}

export async function resolveMultipartCompletionRequest(options: MultipartCompletionRequestOptions): Promise<{ alreadyComplete: boolean }> {
	try {
		const result = await options.completeMultipartUpload();
		return result;
	} catch (error) {
		const classification = options.classifyCompletion(error);
		if (classification === "lost") {
			// Preserve the pre-normalization local-byte probe even though its transition result is unused here.
			try {
				await options.verifyReinitializable();
			} catch (verificationError) {
				console.warn(
					"[Upload] completion byte verification failed while preserving the original completion error",
					verificationError
				);
			}
		}
		throw options.normalize(error);
	}
}

export async function finalizeMultipartCompletion<Session>(options: FinalizeMultipartCompletionOptions<Session>): Promise<boolean> {
	if (!hasValidMultipartParts(options.parts, options.totalChunks)) {
		throw new Error("Refusing to complete multipart upload with non-contiguous or ETag-less parts");
	}
	if (!(await options.ownsLease(options.session))) {
		try {
			options.onOwnershipLost?.("pre-complete", options.session);
		} catch {
			// Ownership-loss diagnostics must not change the fail-closed yield.
		}
		return false;
	}
	await options.completeMultipartUpload(options.parts);
	if (!(await options.ownsLease(options.session))) {
		try {
			options.onOwnershipLost?.("post-complete", options.session);
		} catch {
			// Ownership-loss diagnostics must not change the fail-closed yield.
		}
		return false;
	}
	await options.markTerminalComplete(options.session);
	try {
		await options.cleanupCompleted(options.session);
	} catch (error) {
		try {
			options.onCleanupError?.(error);
		} catch {
			// Cleanup observers cannot undo an already durable completion.
		}
	}
	return true;
}

/**
 * Pure §7 transition. The returned outcome tells adapters which atomic effect
 * to execute; this function never reads or mutates persistence itself.
 */
export function resolveCompletionOutcome(input: CompletionTransitionInput): CompletionOutcome {
	if (input.reconcile === "alreadyComplete") return "success";
	// Fail closed: only an authoritative "lost" classification may enter the recovery branch.
	// "transient" AND any out-of-contract value defer — an ambiguous/garbage classification must
	// never fall through to a progress-destroying reinit or a bytes-gone terminal (§6.1).
	if (input.reconcile !== "lost") return "defer";
	if (input.lostRecoveryPolicy === "fail-fast") return "fail-fast";
	if (input.reinitializationFailure === "buffers-missing") {
		return "bytes-gone";
	}
	if (input.verifyReinitializable === "guaranteed") return "reinitialize";
	if (input.verifyReinitializable === "missing") return "bytes-gone";
	return "defer";
}
